import type { ReactElement, ReactNode } from 'react';
import { Fragment } from 'react';
import type { ScoreContributor } from '@kirin/api-client/contracts/analyses';
import {
  FINDING_SCORE_THRESHOLD,
  HIGH_SCORE_THRESHOLD,
} from '@/components/policylist/constants/builtin-catalog';
import { Link } from 'react-router-dom';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { HiddenCharacterExcerpt } from '../HiddenCharacterExcerpt';
import type { DetectionBlock } from './resolve';

/**
 * The adaptive part of the Detection Logic section (KIR-4272): one renderer per
 * {@link DetectionBlock} kind. Because the resolver decides the *kind*, this
 * component needs no per-alert-type branching — the same section renders a raw
 * regex for a secret finding, a shell command for a dangerous-command finding,
 * and weight chips for an extension finding.
 *
 * Every kind renders as a compact `label | value` row using the SAME row shape
 * as the sibling "VULNERABLE DEPENDENCY" / "DETECTED ON CLIENT" panels
 * (`w-36` muted label column, `text-xs` value). The card is a field sheet, so a
 * detection reason reads as one more field on it rather than as its own stacked
 * mini-section — and a block label like "Matched rule" is a field name, not a
 * heading.
 */

/**
 * Label/value row — deliberately identical to `AlertOsvComponentSection`'s
 * `Row`, so the two sections line up on the same label column when both render.
 */
function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-start gap-2 py-1">
      <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </div>
      <div className="flex min-h-6 min-w-0 flex-1 items-center gap-2">
        {children}
      </div>
    </div>
  );
}

/** Plain prose value. Wraps — reasoning is sentences, not a token. */
function TextValue({ value }: { value: string }): ReactElement {
  return <span className="text-xs break-words">{value}</span>;
}

/**
 * Verbatim mono value with a copy button, sized to sit inside a row.
 *
 * Scrolls horizontally rather than wrapping: a regex or a matched span broken
 * across lines is a different regex or span. `min-w-0` keeps that scroll inside
 * the value column so the card itself never scrolls sideways.
 */
function VerbatimValue({
  value,
  copyLabel,
  copyValue,
}: {
  value: string;
  copyLabel: string;
  /** Full text when `value` is shortened for display — never copy a clipped payload. */
  copyValue?: string | undefined;
}): ReactElement {
  return (
    <>
      <code className="min-w-0 flex-1 overflow-x-auto font-mono text-xs whitespace-pre">
        {value}
      </code>
      <CopyToClipboardButton
        text={copyValue ?? value}
        variant="ghost"
        size="sm"
        compactCopiedState
        className="h-6 w-6 shrink-0 p-0"
        aria-label={copyLabel}
      >
        <span className="sr-only">{copyLabel}</span>
      </CopyToClipboardButton>
    </>
  );
}

function scoreLevel(score: number): string {
  if (score >= HIGH_SCORE_THRESHOLD) return 'High';
  if (score >= FINDING_SCORE_THRESHOLD) return 'Medium';
  return 'Low';
}

/**
 * Narrative under the weight formula — mirrors the KIR-3781 mock (group winners
 * keep max weight; ungrouped fields add). Moved here verbatim from the former
 * standalone `WhyThisFired` section, which this section absorbs.
 */
function buildAggregateExplanation(
  contributors: ScoreContributor[],
  aggregateScore: number
): string {
  const parts: string[] = [];
  for (const contributor of contributors) {
    if (contributor.correlationGroup) {
      parts.push(
        `The ${contributor.correlationGroup} correlation group kept its highest weight (${contributor.weight}).`
      );
    } else {
      parts.push(
        `The ungrouped capability ${contributor.field} added ${contributor.weight}.`
      );
    }
  }
  parts.push(
    `Total ${aggregateScore} crossed the finding threshold of ${FINDING_SCORE_THRESHOLD}.`
  );
  return parts.join(' ');
}

/** One rule + weight pill. Moved from `WhyThisFired` (KIR-3781). */
function ContributorWeightChip({
  contributor,
}: {
  contributor: ScoreContributor;
}): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-1.5 py-0.5">
      <code className="text-[11px] font-medium text-foreground">
        {contributor.field}
      </code>
      <span className="rounded bg-amber-100 px-1 py-px text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
        weight {contributor.weight}
      </span>
    </span>
  );
}

/**
 * The weight formula as a row value: `a + b = 75 (Medium)`.
 *
 * Wraps inside the value column rather than scrolling — the chips are discrete
 * tokens, so a line break between them loses nothing.
 */
function ChipsValue({
  contributors,
  score,
}: {
  contributors: ScoreContributor[];
  score?: number;
}): ReactElement {
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {contributors.map((contributor, index) => (
        <Fragment key={`${contributor.ruleId}-${contributor.field}`}>
          {index > 0 && (
            <span className="text-xs text-muted-foreground" aria-hidden>
              +
            </span>
          )}
          <ContributorWeightChip contributor={contributor} />
        </Fragment>
      ))}
      {score !== undefined && (
        <span className="text-xs font-semibold text-foreground">
          = {score} ({scoreLevel(score)})
        </span>
      )}
    </span>
  );
}

/** Renders one block as one or more rows. The `kind` switch is exhaustive. */
export function DetectionBlockView({
  block,
}: {
  block: DetectionBlock;
}): ReactElement {
  switch (block.kind) {
    case 'text':
      return (
        <div data-testid="detection-block-text">
          <Row label={block.label}>
            <TextValue value={block.value} />
          </Row>
        </div>
      );
    case 'code':
      return (
        <div data-testid="detection-block-code">
          <Row label={block.label}>
            <VerbatimValue
              value={block.value}
              copyValue={block.copyValue}
              copyLabel="Copy matched value"
            />
          </Row>
        </div>
      );
    case 'excerpt':
      // Wraps rather than scrolling, unlike `code`: the marked region and its
      // context have to be visible together.
      return (
        <div data-testid="detection-block-excerpt">
          <Row label={block.label}>
            <HiddenCharacterExcerpt
              value={block.value}
              className="min-w-0 flex-1 text-xs"
            />
            <CopyToClipboardButton
              text={block.value}
              variant="ghost"
              size="sm"
              compactCopiedState
              className="h-6 w-6 shrink-0 p-0"
              aria-label="Copy surrounding text"
            >
              <span className="sr-only">Copy surrounding text</span>
            </CopyToClipboardButton>
          </Row>
        </div>
      );
    case 'rule':
      // The pattern gets its own row rather than sitting under the name: it is a
      // separate fact (the regex that fired vs. what it is called), and only the
      // raw-pattern engines have one.
      return (
        <div data-testid="detection-block-rule">
          <Row label={block.label}>
            <TextValue value={block.ruleName} />
          </Row>
          {block.pattern != null && (
            <Row label="Pattern">
              <VerbatimValue
                value={block.pattern}
                copyLabel="Copy rule pattern"
              />
            </Row>
          )}
        </div>
      );
    case 'kv':
      // The block label ("Detection engine", "Key factors") is a grouping word
      // whose rows already name themselves ("Engine", "Factor 1"), so the rows
      // go straight into the sheet instead of nesting under a heading.
      return (
        <div data-testid="detection-block-kv">
          {block.rows.map(row => (
            <Row key={row.k} label={row.k}>
              <TextValue value={row.v} />
            </Row>
          ))}
        </div>
      );
    case 'link':
      // <Link>, not an anchor: a full page load drops the alert-list filters.
      return (
        <div data-testid="detection-block-link">
          <Row label={block.label}>
            <Link
              to={block.to}
              className="text-xs text-primary underline underline-offset-2 hover:no-underline"
            >
              {block.text}
            </Link>
          </Row>
        </div>
      );
    case 'chips':
      return (
        <div data-testid="detection-block-chips">
          <Row label={block.label}>
            <ChipsValue
              contributors={block.contributors}
              {...(block.score !== undefined ? { score: block.score } : {})}
            />
          </Row>
          <Row label="Calculation">
            <TextValue
              value={
                block.score !== undefined
                  ? buildAggregateExplanation(block.contributors, block.score)
                  : 'Instant signal: this field carries weight 100.'
              }
            />
          </Row>
        </div>
      );
  }
}

DetectionBlockView.displayName = 'DetectionBlockView';
